import { ChevronDownIcon } from 'lucide-react-native';
import * as React from 'react';
import { type ReactNode } from 'react';
import { Pressable } from 'react-native';
import { isWeb, XStack, YStack } from 'tamagui';
import { LinearGradient } from 'tamagui/linear-gradient';

import { GRADIENT_COLOR_BOTTOM, GRADIENT_COLOR_TOP } from '../../constants';
import { Icon, type IconType } from '../icons/Icon';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
} from '../text/Typography';

/**
 * ExpandingTilePreview - An expanded tile showing preview content that expands into full SheetDialog
 * Shows eyebrow, expand button, title, and preview content with smooth fade effect
 */
export interface ExpandingTilePreviewProps {
    eyebrow?: string;
    title: ReactNode;
    previewContent: ReactNode;
    onPress: () => void;
    /** Corner button icon. Defaults to a chevron-down, matching the sheet-expand affordance. */
    icon?: IconType;
    /** Optional illustration/graphic rendered between the header row and the title. */
    graphic?: ReactNode;
    /** Whether to clamp and fade the bottom of previewContent. Set false for short, fully-visible content. */
    fade?: boolean;
    /** Remove bottom padding from the content area. */
    noPaddingBottom?: boolean;
}

/** Lets vertical scroll win over tap when the tile sits inside a ScrollView. */
const SCROLL_PRESS_DELAY_MS = 80;

export const ExpandingTilePreview = ({
    eyebrow,
    title,
    previewContent,
    onPress,
    icon = ChevronDownIcon,
    graphic,
    fade = true,
    noPaddingBottom = false,
}: ExpandingTilePreviewProps) => {
    return (
        <Pressable
            onPress={onPress}
            unstable_pressDelay={isWeb ? undefined : SCROLL_PRESS_DELAY_MS}
            style={{ width: '100%' }}
        >
            <YStack
                width="100%"
                backgroundColor="transparent"
                borderRadius={24}
                overflow="hidden"
                position="relative"
                shadowColor="$black"
                shadowOpacity={0.06}
                shadowOffset={{ width: 0, height: 8 }}
                shadowRadius={16}
            >
                {/* Blue gradient background - extends fully */}
                <YStack
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                >
                    <LinearGradient
                        colors={[GRADIENT_COLOR_TOP, GRADIENT_COLOR_BOTTOM]}
                        start={[0, 0]}
                        end={[0, 1]}
                        width="100%"
                        height="100%"
                    />
                </YStack>

                {/* Header with eyebrow and expand button */}
                <XStack
                    paddingHorizontal={24}
                    paddingTop={20}
                    alignItems="center"
                    justifyContent="space-between"
                    zIndex={1}
                >
                    {eyebrow != null && eyebrow !== '' && (
                        <YStack
                            backgroundColor="$white"
                            paddingHorizontal={12}
                            paddingVertical={6}
                            borderRadius={16}
                        >
                            <BodySmallRegular color="$black">
                                {eyebrow}
                            </BodySmallRegular>
                        </YStack>
                    )}
                    <XStack marginLeft="auto">
                        <YStack
                            backgroundColor="$gray200"
                            padding={8}
                            borderRadius={16}
                        >
                            <Icon icon={icon} size={16} color="$black" />
                        </YStack>
                    </XStack>
                </XStack>

                {/* Content with fade effect */}
                <YStack
                    paddingHorizontal={24}
                    paddingTop={16}
                    paddingBottom={noPaddingBottom ? 0 : 24}
                    zIndex={1}
                    gap={12}
                >
                    {graphic}

                    {title !== '' &&
                        (typeof title === 'string' ? (
                            <BodyLargeEmphasized color="$black">
                                {title}
                            </BodyLargeEmphasized>
                        ) : (
                            title
                        ))}

                    {/* Preview content, optionally clamped with a fade mask for longer/truncated content */}
                    {previewContent !== '' && (
                        <YStack
                            position="relative"
                            maxHeight={fade ? 100 : undefined}
                            overflow={fade ? 'hidden' : 'visible'}
                        >
                            {typeof previewContent === 'string' ? (
                                <BodyMediumRegular
                                    color="$gray1000"
                                    lineHeight={24}
                                >
                                    {previewContent}
                                </BodyMediumRegular>
                            ) : (
                                previewContent
                            )}
                        </YStack>
                    )}
                    {/* Multi-step gradient for smoother fade matching blue background */}
                    {fade && (
                        <LinearGradient
                            colors={[
                                'rgba(180, 208, 216, 0)',
                                'rgba(180, 208, 216, 0.3)',
                                'rgba(180, 208, 216, 0.7)',
                                'rgba(180, 208, 216, 1)',
                            ]}
                            locations={[0, 0.2, 0.5, 1]}
                            start={[0, 0]}
                            end={[0, 1]}
                            position="absolute"
                            bottom={0}
                            left={0}
                            right={0}
                            height={60}
                            pointerEvents="none"
                        />
                    )}
                </YStack>
            </YStack>
        </Pressable>
    );
};
